import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Bug, CheckCircle2, Inbox, Lightbulb, Link2, MessageSquare, Paperclip, Sparkles, X,
} from 'lucide-react';

import {
  loadFeedbackMedia, loadFeedbackReport, loadFeedbackReports, setFeedbackStatus,
} from './api';
import { useXomicsAuth } from './AuthContext';
import { Empty, Failed, Head, Loading, Pill } from './parts';
import { FilterMenu, NUMBER, ago, useNow, when } from './MobileParts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './feedback-inbox.css';

const PAGE = 25;

// Keys, not words: these are read when the module loads, before the catalogue
// has arrived. About and kind reuse the submit page's labels so the two screens
// cannot drift apart.
const STATUS = [
  { value: 'new', label: 'ui.feedback_inbox.status_new', tone: 'warn' },
  { value: 'reviewed', label: 'ui.feedback_inbox.status_seen', tone: 'info' },
  { value: 'resolved', label: 'ui.feedback_inbox.status_fixed', tone: 'ok' },
];

const ABOUT = [
  { value: 'mobile_app', label: 'ui.feedback.about_mobile_app' },
  { value: 'xomics_portal', label: 'ui.feedback.about_xomics_portal' },
  { value: 'clinic_portal', label: 'ui.feedback.about_clinic_portal' },
  { value: 'store_website', label: 'ui.feedback.about_store_website' },
  { value: 'other', label: 'ui.feedback.about_other' },
];

const KIND = [
  { value: 'bug', label: 'ui.feedback.kind_bug' },
  { value: 'idea', label: 'ui.feedback.kind_idea' },
  { value: 'other', label: 'ui.feedback.kind_other' },
];

const KIND_ICON = { bug: Bug, idea: Lightbulb, other: MessageSquare };

const labelOf = (list, value) => {
  const key = list.find((entry) => entry.value === value)?.label;
  return key ? tx(key) : value;
};
const toneOf = (value) => STATUS.find((entry) => entry.value === value)?.tone ?? 'flat';

function Stat({ icon: Icon, tone, label, value, detail }) {
  return (
    <div className="xo-fbinbox-stat">
      <span className={`xo-fbinbox-stat-icon t-${tone}`}><Icon aria-hidden="true" /></span>
      <span>
        <small>{label}</small>
        <strong>{NUMBER.format(value)}</strong>
        {detail ? <em>{detail}</em> : null}
      </span>
    </div>
  );
}

function Fields({ title, rows }) {
  const shown = rows.filter(([, value]) => value !== null && value !== undefined && value !== '');
  if (shown.length === 0) return null;
  return (
    <section>
      <h3 className="xo-mob-subhead">{title}</h3>
      <dl className="xo-fbinbox-fields">
        {shown.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
      </dl>
    </section>
  );
}

// The attachment is a link that expires, so it is asked for when a report is
// opened rather than for every row of the list.
function Attachment({ uuid, contentType }) {
  const [media, setMedia] = useState(null);
  const [failed, setFailed] = useState('');

  useEffect(() => {
    let current = true;
    setMedia(null);
    setFailed('');
    loadFeedbackMedia(uuid)
      .then((answer) => { if (current) setMedia(answer); })
      .catch((error) => {
        if (current) setFailed(error?.message || tx('ui.feedback_inbox.attachment_failed'));
      });
    return () => { current = false; };
  }, [uuid]);

  if (failed) return <p className="xo-fbinbox-note">{failed}</p>;
  if (media === null) return <p className="xo-fbinbox-note">{tx('ui.feedback_inbox.loading_attachment')}</p>;
  return (
    <figure className="xo-fbinbox-media">
      {String(contentType || '').startsWith('video/')
        ? <video src={media.url} controls preload="metadata" />
        : <img src={media.url} alt={tx('ui.feedback_inbox.attachment_alt')} />}
    </figure>
  );
}

// A panel beside the list rather than over it: the list stays readable, and the
// page uses the width it already has.
function ReportDrawer({ uuid, onClose, onChanged }) {
  const [busy, setBusy] = useState('');
  const [failed, setFailed] = useState('');
  const { permissions } = useXomicsAuth();
  const mayOpenAccount = permissions.includes('PLATFORM_MOBILE_READ');
  const load = useCallback(() => loadFeedbackReport(uuid), [uuid]);
  const opened = useLiveResource([], load, { reconcileMs: 0 });
  const data = opened.data;
  const report = data?.report;

  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  async function move(status) {
    setBusy(status);
    setFailed('');
    try {
      await setFeedbackStatus(uuid, status);
      await opened.refresh();
      onChanged();
    } catch (error) {
      setFailed(error?.message || tx('ui.feedback_inbox.status_failed'));
    } finally {
      setBusy('');
    }
  }

  return (
    <motion.aside className="xo-fbinbox-drawer" role="dialog" aria-modal="false"
      aria-label={tx('ui.feedback_inbox.report')}
      initial={{ x: 32, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 32, opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}>
      <header>
        <div>
          <h2>{report ? labelOf(ABOUT, report.about) : tx('ui.feedback_inbox.report')}</h2>
          {report && <p>{when(report.createdAt)}</p>}
        </div>
        {report && <Pill tone={toneOf(report.status)}>{labelOf(STATUS, report.status)}</Pill>}
        <button type="button" className="xo-mob-icon-button" aria-label={tx('ui.feedback_inbox.close')}
          onClick={onClose}><X /></button>
      </header>

      <div className="xo-fbinbox-drawer-body">
        {opened.loading && data === null && <Loading label={tx('ui.feedback_inbox.reading_report')} />}
        {opened.error && data === null && <Failed message={opened.error} onRetry={opened.refresh} />}

        {report && (
          <>
            <section>
              <h3 className="xo-mob-subhead">{tx('ui.feedback_inbox.what_happened')}</h3>
              <p className="xo-fbinbox-message">{report.message}</p>
            </section>

            {report.hasMedia && <Attachment uuid={uuid} contentType={report.mediaContentType} />}
            {report.mediaRemovedAt
              ? <p className="xo-fbinbox-note">{tx('ui.feedback_inbox.attachment_removed', { value: ago(report.mediaRemovedAt) })}</p>
              : null}

            <Fields title={tx('ui.feedback_inbox.the_report')} rows={[
              [tx('ui.feedback_inbox.field_where'), report.area],
              [tx('ui.feedback_inbox.field_kind'), labelOf(KIND, report.kind)],
              [tx('ui.feedback_inbox.field_sent_by'),
                report.sentBy?.name || report.sentBy?.email || tx('ui.feedback_inbox.not_recorded')],
              [tx('ui.feedback_inbox.field_reply_to'), report.contactEmail],
            ]} />

            <Fields title={tx('ui.feedback_inbox.the_app')} rows={[
              [tx('ui.feedback_inbox.field_app_version'), report.appVersion],
              [tx('ui.feedback_inbox.field_phone'), report.deviceModel],
              [tx('ui.feedback_inbox.field_system'), report.systemVersion],
              [tx('ui.feedback_inbox.field_app_account'), report.mobileAccountUuid && (mayOpenAccount
                ? (
                  <Link className="xo-mob-link" to={`/xomics/mobile/accounts?account=${encodeURIComponent(report.mobileAccountUuid)}`}>
                    {tx('ui.feedback_inbox.open_app_account')}<Link2 aria-hidden="true" />
                  </Link>
                )
                : <code>{report.mobileAccountUuid}</code>)],
            ]} />

            {data.history.length > 0 && (
              <section>
                <h3 className="xo-mob-subhead">{tx('ui.feedback_inbox.history')}</h3>
                <ol className="xo-fbinbox-history">
                  {data.history.map((event) => (
                    <li key={`${event.at}-${event.to}`}>
                      <strong>{labelOf(STATUS, event.from)} → {labelOf(STATUS, event.to)}</strong>
                      <small>{when(event.at)}{event.by ? ` · ${event.by}` : ''}</small>
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </>
        )}
      </div>

      {report && (
        <footer className="xo-fbinbox-drawer-actions">
          {failed && <p className="xo-fbinbox-failed" role="alert">{failed}</p>}
          <div>
            {STATUS.filter((entry) => entry.value !== report.status).map((entry) => (
              <button key={entry.value} type="button" className="xo-btn-2" disabled={busy !== ''}
                onClick={() => move(entry.value)}>
                {busy === entry.value
                  ? tx('ui.feedback_inbox.saving')
                  : tx('ui.feedback_inbox.mark_as', { value: tx(entry.label).toLowerCase() })}
              </button>
            ))}
          </div>
        </footer>
      )}
    </motion.aside>
  );
}

export function FeedbackInbox() {
  const [about, setAbout] = useState('');
  const [status, setStatus] = useState('');
  const [before, setBefore] = useState(null);
  const [opened, setOpened] = useState(null);
  const [counts, setCounts] = useState(null);
  const now = useNow();

  const load = useCallback(() => loadFeedbackReports({
    about, status, limit: PAGE, beforeAt: before?.at, beforeId: before?.id,
  }), [about, status, before]);
  const list = useLiveResource(['feedback'], load, { reconcileMs: 60000 });
  const data = list.data;

  // The counts come back only on the first page; hold the last set so they do
  // not blink away while paging back.
  useEffect(() => { if (data?.counts) setCounts(data.counts); }, [data]);

  // A marker from one filter means nothing under another.
  const choose = (setter) => (value) => { setBefore(null); setter(value); };

  const tabs = [
    { value: '', label: tx('ui.feedback_inbox.tab_all'), count: counts?.total },
    ...STATUS.map((entry) => ({ value: entry.value, label: tx(entry.label), count: counts?.[entry.value] })),
  ];

  return (
    <section className="xo-fbinbox">
      <Head title={tx('ui.feedback_inbox.title')} subtitle={tx('ui.feedback_inbox.subtitle')} />

      {counts && (
        <div className="xo-fbinbox-stats">
          <Stat icon={Inbox} tone="flat" label={tx('ui.feedback_inbox.stat_total')} value={counts.total} />
          <Stat icon={Sparkles} tone="warn" label={tx('ui.feedback_inbox.stat_new')} value={counts.new}
            detail={tx('ui.feedback_inbox.stat_new_detail')} />
          <Stat icon={CheckCircle2} tone="ok" label={tx('ui.feedback_inbox.stat_fixed')} value={counts.resolved} />
          <Stat icon={Paperclip} tone="info" label={tx('ui.feedback_inbox.stat_with_file')} value={counts.withMedia} />
        </div>
      )}

      <div className="xo-fbinbox-bar">
        <div className="xo-fbinbox-tabs" role="tablist">
          {tabs.map((tab) => (
            <button key={tab.value || 'all'} type="button" role="tab" aria-selected={status === tab.value}
              className={status === tab.value ? 'is-active' : ''} onClick={() => choose(setStatus)(tab.value)}>
              {tab.label}
              {tab.count === undefined ? null : <span>{NUMBER.format(tab.count)}</span>}
            </button>
          ))}
        </div>
        <FilterMenu icon={Inbox} label={tx('ui.feedback_inbox.about')} value={about}
          onChange={choose(setAbout)}
          options={[{ value: '', label: tx('ui.feedback_inbox.anything') },
            ...ABOUT.map((entry) => ({ value: entry.value, label: tx(entry.label) }))]} />
      </div>

      <div className={`xo-fbinbox-split${opened ? ' is-open' : ''}`}>
        <div className="xo-fbinbox-main">
          {list.loading && data === null && <Loading label={tx('ui.feedback_inbox.reading_inbox')} />}
          {list.error && data === null && <Failed message={list.error} onRetry={list.refresh} />}
          {data && data.reports.length === 0
            && <Empty title={tx('ui.feedback_inbox.nothing_here')} detail={tx('ui.feedback_inbox.nothing_matches')} />}

          {data && data.reports.length > 0 && (
            <ul className="xo-fbinbox-list">
              {data.reports.map((report) => {
                const Icon = KIND_ICON[report.kind] ?? MessageSquare;
                return (
                  <li key={report.uuid}>
                    <button type="button"
                      className={`xo-fbinbox-row${opened === report.uuid ? ' is-active' : ''}`}
                      onClick={() => setOpened(report.uuid)}>
                      <span className="xo-fbinbox-icon"><Icon aria-hidden="true" /></span>
                      <span className="xo-fbinbox-what">
                        <strong>{report.area}</strong>
                        <small>{labelOf(ABOUT, report.about)} · {labelOf(KIND, report.kind)}</small>
                      </span>
                      <span className="xo-fbinbox-who">
                        <strong>{report.sentBy?.name || report.sentBy?.email || report.contactEmail}</strong>
                        <small>{ago(report.createdAt, now)}</small>
                      </span>
                      {report.hasMedia
                        ? <Paperclip className="xo-fbinbox-clip" aria-label={tx('ui.feedback_inbox.has_attachment')} />
                        : <span className="xo-fbinbox-clip" />}
                      <Pill tone={toneOf(report.status)}>{labelOf(STATUS, report.status)}</Pill>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {data?.nextBefore && (
            <div className="xo-fbinbox-more">
              <button type="button" className="xo-btn-2" onClick={() => setBefore(data.nextBefore)}>
                {tx('ui.feedback_inbox.older')}
              </button>
            </div>
          )}
          {before !== null && (
            <div className="xo-fbinbox-more">
              <button type="button" className="xo-btn-2" onClick={() => setBefore(null)}>
                {tx('ui.feedback_inbox.back_to_newest')}
              </button>
            </div>
          )}
        </div>

        <AnimatePresence>
          {opened && (
            <ReportDrawer key={opened} uuid={opened} onClose={() => setOpened(null)} onChanged={list.refresh} />
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

export default FeedbackInbox;
